import {engineContext} from './context.js';

(function(global){'use strict';
const VERSION='0.1.0-alpha';

function normTask(x){
  x=x||{};
  return Object.freeze({
    taskId:x.taskId||null,
    symbol:x.symbol||null,
    conclusionIds:Array.isArray(x.conclusionIds)?[...new Set(x.conclusionIds.filter(Boolean))]:[],
    evidenceIds:Array.isArray(x.evidenceIds)?[...new Set(x.evidenceIds.filter(Boolean))]:[],
    completionState:['OPEN','COMPLETED','PARTIAL','BLOCKED'].includes(x.completionState)?x.completionState:'OPEN',
    evidenceFingerprint:x.evidenceFingerprint||null,
    completedFingerprint:x.completedFingerprint||null,
    priority:Number.isFinite(x.priority)?x.priority:0,
    taskType:x.taskType||'RESEARCH',
    nextAction:x.nextAction||null
  });
}

function normInvalidation(x){
  x=x||{};
  return Object.freeze({
    conclusionId:x.conclusionId||x.id||null,
    action:x.action||'REVIEW_EVIDENCE',
    evidenceIds:Array.isArray(x.evidenceIds)?[...new Set(x.evidenceIds.filter(Boolean))]:[]
  });
}

function strongestAction(actions){
  const order={INVALIDATE:5,REVIEW_CONFLICT:4,REVALIDATE:3,REVIEW_EVIDENCE:2,NONE:0};
  let best='NONE',score=0;
  for(const a of actions||[]){
    const s=order[a]||0;
    if(s>score){best=a;score=s;}
  }
  return best;
}

function affected(task,invalidations){
  const t=normTask(task), inv=(Array.isArray(invalidations)?invalidations:[]).map(normInvalidation);
  const tCon=new Set(t.conclusionIds), tEv=new Set(t.evidenceIds);
  const matches=[];
  for(const i of inv){
    if((i.conclusionId&&tCon.has(i.conclusionId)) || i.evidenceIds.some(e=>tEv.has(e))) matches.push(i);
  }
  return Object.freeze(matches);
}

function reopenDecision(task,invalidations){
  const t=normTask(task), matches=affected(t,invalidations);
  if(!matches.length) return Object.freeze({reopen:false,reason:'UNAFFECTED',action:'NONE'});
  const action=strongestAction(matches.map(x=>x.action));
  if(action==='REVALIDATE'){
    return Object.freeze({reopen:true,reason:'DEPENDENCY_REVALIDATION_REQUIRED',action});
  }
  if(['INVALIDATE','REVIEW_CONFLICT','REVIEW_EVIDENCE'].includes(action)){
    return Object.freeze({reopen:true,reason:'DEPENDENCY_CHANGED',action});
  }
  return Object.freeze({reopen:false,reason:'NO_ACTION',action});
}

function reopenTasks(tasks,invalidations){
  const out=[];
  for(const raw of Array.isArray(tasks)?tasks:[]){
    const t=normTask(raw), d=reopenDecision(t,invalidations);
    if(!d.reopen) continue;
    out.push(Object.freeze({
      ...t,
      previousCompletionState:t.completionState,
      completionState:'OPEN',
      reopenReason:d.reason,
      dependencyAction:d.action
    }));
  }
  out.sort((a,b)=>b.priority-a.priority||(a.symbol||'').localeCompare(b.symbol||''));
  return Object.freeze(out);
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const taskA={taskId:'A',symbol:'AAA',conclusionIds:['c1'],evidenceIds:['e1'],completionState:'COMPLETED',priority:10};
  const taskB={taskId:'B',symbol:'AAA',conclusionIds:['c2'],evidenceIds:['e2'],completionState:'COMPLETED',priority:5};
  const inv1={conclusionId:'c1',action:'INVALIDATE',evidenceIds:['e1']};

  ok('direct conclusion dependency detected',affected(taskA,[inv1]).length===1);
  ok('unrelated task remains unaffected',affected(taskB,[inv1]).length===0);
  ok('invalidated dependency reopens task',reopenDecision(taskA,[inv1]).reopen===true);
  ok('unrelated task stays closed',reopenDecision(taskB,[inv1]).reopen===false);
  ok('reopened task becomes OPEN',reopenTasks([taskA],[inv1])[0].completionState==='OPEN');
  ok('previous completion state preserved',reopenTasks([taskA],[inv1])[0].previousCompletionState==='COMPLETED');

  const invConflict={conclusionId:'c1',action:'REVIEW_CONFLICT'};
  ok('conflict reopens dependent task',reopenDecision(taskA,[invConflict]).reopen===true);

  const invReview={conclusionId:'c1',action:'REVIEW_EVIDENCE'};
  ok('evidence review reopens dependent task',reopenDecision(taskA,[invReview]).reopen===true);

  const invReval={conclusionId:'c1',action:'REVALIDATE'};
  ok('revalidation reopens dependent task for confirmation',reopenDecision(taskA,[invReval]).reason==='DEPENDENCY_REVALIDATION_REQUIRED');

  const eOnly={conclusionId:'other',action:'REVIEW_EVIDENCE',evidenceIds:['e1']};
  ok('evidence dependency alone can reopen task',reopenDecision(taskA,[eOnly]).reopen===true);

  const multi=[{conclusionId:'c1',action:'REVIEW_EVIDENCE'},{conclusionId:'c1',action:'INVALIDATE'}];
  ok('strongest dependency action wins',reopenDecision(taskA,multi).action==='INVALIDATE');

  const ranked=reopenTasks([{...taskA,priority:1},{...taskB,conclusionIds:['c1'],priority:20}],[inv1]);
  ok('reopened tasks preserve priority ordering',ranked[0].taskId==='B');
  ok('no whole-company reopening',reopenTasks([taskA,taskB],[inv1]).length===1);
  ok('empty invalidation list reopens nothing',reopenTasks([taskA,taskB],[]).length===0);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,normTask,normInvalidation,strongestAction,affected,reopenDecision,reopenTasks,selfTest};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestDependencyAwareResearchReopenEngine=Object.freeze(API);
})(engineContext);